import LegalPage from '../components/LegalPage';

const sections = [
  {
    title: 'Introduction',
    content: (
      <div className="space-y-4">
        <p>Thalamus Labs Ltd ("we," "us," or "our") operates the NeuroMesh platform, including the neuro-mesh.io website, the NeuroMesh protocol, and related services (collectively, the "Services"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Services.</p>
        <p>We are committed to protecting your privacy. By using NeuroMesh, you agree to the collection and use of information in accordance with this policy.</p>
      </div>
    ),
  },
  {
    title: 'Information We Collect',
    content: (
      <div className="space-y-6">
        <div>
          <h3 style={{ color: '#ccc', fontSize: '14px', fontWeight: 600, marginBottom: '10px', fontFamily: "'Inter', sans-serif" }}>Personal Information</h3>
          <ul style={{ listStyleType: 'none', padding: 0, space: '8px' }} className="space-y-2">
            {[
              ['Contact Information', 'Name, email address, wallet addresses when you connect a Web3 wallet'],
              ['Account Information', 'Username, profile information you provide'],
              ['Communication Data', 'Records of communications when you contact us'],
            ].map(([label, text]) => (
              <li key={label} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ color: '#00F0FF', fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", paddingTop: '3px', flexShrink: 0 }}>{label}:</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 style={{ color: '#ccc', fontSize: '14px', fontWeight: 600, marginBottom: '10px', fontFamily: "'Inter', sans-serif" }}>Technical Information</h3>
          <ul style={{ listStyleType: 'none', padding: 0 }} className="space-y-2">
            {[
              ['Device Data', 'IP address, browser type, operating system'],
              ['Usage Data', 'Pages visited, time spent, features used'],
              ['Blockchain Data', 'Wallet addresses, transaction hashes, on-chain interactions (public by nature)'],
            ].map(([label, text]) => (
              <li key={label} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ color: '#4A90D9', fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", paddingTop: '3px', flexShrink: 0 }}>{label}:</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 style={{ color: '#ccc', fontSize: '14px', fontWeight: 600, marginBottom: '10px', fontFamily: "'Inter', sans-serif" }}>Robot & Sensor Data (Enterprise/Terminal Users)</h3>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px' }} className="space-y-1">
            <li>Robot telemetry data, performance metrics, system logs</li>
            <li>Sensor readings and attribution data</li>
            <li>Computation credits and usage metrics</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    title: 'How We Use Your Information',
    content: (
      <ul style={{ listStyleType: 'none', padding: 0 }} className="space-y-2">
        {[
          'Provide, operate, and maintain our Services',
          'Improve, personalize, and expand our Services',
          'Understand and analyze how you use our Services',
          'Communicate with you for customer service, updates, and marketing',
          'Send relevant technical documentation and product announcements',
          'Process transactions and manage token/wallet connections',
          'Comply with legal obligations',
        ].map((item, i) => (
          <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <span style={{ color: '#00F0FF', flexShrink: 0, marginTop: '6px' }}>&#9472;</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    ),
  },
  {
    title: 'Data Sharing & Disclosure',
    content: (
      <div className="space-y-4">
        <p style={{ color: '#00FF88', fontFamily: "'JetBrains Mono', monospace", fontSize: '13px' }}>We do NOT sell your personal data.</p>
        <p>We may share information with:</p>
        <ul style={{ listStyleType: 'none', padding: 0 }} className="space-y-2">
          {[
            ['Service Providers', 'Third parties who help us operate (hosting, analytics, customer support)'],
            ['Legal Authorities', 'When required by law, subpoena, or government regulation'],
            ['Business Transfers', 'In connection with mergers, acquisitions, or asset sales'],
            ['Smart Contract Interactions', 'On-chain data is public and cannot be unshared'],
          ].map(([label, text]) => (
            <li key={label} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <span style={{ color: '#4A90D9', fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", paddingTop: '3px', flexShrink: 0 }}>{label}:</span>
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    title: 'Data Retention',
    content: (
      <ul style={{ listStyleType: 'none', padding: 0 }} className="space-y-2">
        {[
          ['Account Data', 'Retained while your account is active; deleted upon request'],
          ['Technical Data', 'Retained for up to 12 months for analytics'],
          ['Blockchain Data', 'Cannot be deleted (public ledger)'],
          ['Robot Telemetry', 'Per enterprise contract terms'],
        ].map(([label, text]) => (
          <li key={label} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <span style={{ color: '#00F0FF', fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", paddingTop: '3px', flexShrink: 0 }}>{label}:</span>
            <span>{text}</span>
          </li>
        ))}
      </ul>
    ),
  },
  {
    title: 'Your Rights',
    content: (
      <div className="space-y-4">
        <p>Under applicable law (GDPR, CCPA, UAE PDPL), you may have the right to:</p>
        <ul style={{ listStyleType: 'none', padding: 0 }} className="space-y-2">
          {[
            'Access the personal data we hold about you',
            'Request correction of inaccurate data',
            'Request deletion of your personal data (where not prohibited by law)',
            'Object to processing of your data',
            'Request restriction of processing',
            'Data portability',
            'Withdraw consent at any time',
          ].map((item, i) => (
            <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <span style={{ color: '#00F0FF', flexShrink: 0, marginTop: '6px' }}>&#9472;</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p>To exercise these rights, contact: <a href="mailto:legal@neuro-mesh.io" style={{ color: '#00F0FF' }}>legal@neuro-mesh.io</a></p>
      </div>
    ),
  },
  {
    title: 'Security',
    content: (
      <div className="space-y-4">
        <p>We implement appropriate technical and organizational measures to protect your data, including:</p>
        <ul style={{ listStyleType: 'none', padding: 0 }} className="space-y-2">
          {['Encryption of data in transit and at rest', 'Regular security assessments', 'Access controls and authentication', 'Secure development practices'].map((item, i) => (
            <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <span style={{ color: '#00FF88', flexShrink: 0, marginTop: '6px' }}>&#9472;</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p style={{ color: '#666', fontSize: '13px' }}>No method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.</p>
      </div>
    ),
  },
  {
    title: 'International Data Transfers',
    content: <p>Your information may be transferred to servers outside your jurisdiction. We ensure appropriate safeguards are in place, including Standard Contractual Clauses or adequacy decisions where required.</p>,
  },
  {
    title: 'Cookies & Tracking',
    content: (
      <div className="space-y-4">
        <p>We use cookies and similar tracking technologies to:</p>
        <ul style={{ listStyleType: 'disc', paddingLeft: '20px' }} className="space-y-1">
          <li>Keep you logged in</li>
          <li>Understand user behavior</li>
          <li>Improve our Services</li>
        </ul>
        <p>You can disable cookies through your browser settings.</p>
      </div>
    ),
  },
  {
    title: 'Children\'s Privacy',
    content: <p>Our Services are not intended for children under 13. We do not knowingly collect data from children.</p>,
  },
  {
    title: 'Changes to This Policy',
    content: <p>We may update this policy periodically. We will notify you of material changes via email or prominent notice on our website.</p>,
  },
  {
    title: 'Contact Us',
    content: (
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '13px' }} className="space-y-1">
        <p style={{ color: '#fff' }}>Thalamus Labs Ltd</p>
        <p>Intershore Chambers</p>
        <p>Road Town, Tortola</p>
        <p>British Virgin Islands</p>
        <p style={{ marginTop: '12px' }}>Privacy concerns: <a href="mailto:legal@neuro-mesh.io" style={{ color: '#00F0FF' }}>legal@neuro-mesh.io</a></p>
      </div>
    ),
  },
];

export default function PrivacyPolicy() {
  return <LegalPage title="Privacy Policy" lastUpdated="February 2026" sections={sections} />;
}
